<template>
  <panel class="case-ivs" color="#60c1d8" title="IV" data-testid="iv-panel">
    <div class="left-column">
      <toggle-switch
        :value="caseConf.iv.enabled"
        :readonly="readonly"
        aria-label="IV Toggle"
        data-testid="iv-toggle"
        @toggle="updateEnabled"
      />
      <iv-diagram
        :case-conf="caseConf"
        :readonly="readonly || !caseConf.iv.enabled"
      />
    </div>
    <help-tooltip v-if="ivEnabledError" type="error" aria-label="IV enabled error" data-testid="iv-enabled-error">
      {{ ivEnabledError.message }}
    </help-tooltip>
    <div class="accordion" data-testid="accordion-container">
      <div class="accordion-item" data-testid="iv-bag-accordion-container">
        <md-button
          class="md-raised accordion-trigger"
          :class="{ open: isActivePanel('IV_BAG') }"
          :disabled="!caseConf.iv.enabled"
          aria-label="IV Bag"
          data-testid="iv-bag-accordion-button"
          @click="togglePanel('IV_BAG')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Bag</label>
        </md-button>
      </div>
      <transition name="fold">
        <div v-if="isActivePanel('IV_BAG')" class="accordion-content" data-testid="iv-total-number-of-bags-container">
          <iv-bag-panel
            :iv="caseConf.iv"
            :readonly="readonly"
            @updateIv="emitUpdate"
          />
        </div>
      </transition>
      <div class="accordion-item" data-testid="iv-control-accordion-container">
        <md-button
          class="md-raised accordion-trigger"
          :class="{ open: isActivePanel('IV_CONTROL') }"
          :disabled="!caseConf.iv.enabled"
          aria-label="IV Control"
          data-testid="iv-control-accordion-button"
          @click="togglePanel('IV_CONTROL')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Control</label>
        </md-button>
      </div>
      <transition name="fold">
        <div v-if="isActivePanel('IV_CONTROL') && caseConf.iv.dropSet" class="accordion-content" data-testid="iv-control-container">
          <iv-control-panel
            :iv="caseConf.iv"
            :readonly="readonly"
            @updateIv="emitUpdate"
          />
        </div>
      </transition>
      <div class="accordion-item" data-testid="iv-types-accordion-container">
        <md-button
          class="md-raised accordion-trigger"
          :class="{ open: isActivePanel('IV_TYPES') }"
          :disabled="!caseConf.iv.enabled"
          aria-label="IV Types and Placement"
          data-testid="iv-types-accordion-button"
          @click="togglePanel('IV_TYPES')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Types & Placement</label>
        </md-button>
      </div>
      <transition name="fold">
        <div v-if="isActivePanel('IV_TYPES') && caseConf.iv.types" class="accordion-content" data-testid="iv-types-container">
          <iv-types-panel
            :iv="caseConf.iv"
            :readonly="readonly"
            :navigation="navigation"
            @updateIv="emitUpdate"
          />
        </div>
      </transition>
    </div>
  </panel>
</template>

<script>
import { debounce, merge, get } from 'lodash';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import Panel from '../../common/Panel';
import ToggleSwitch from '../../common/ToggleSwitch';
import IvDiagram from './IvDiagram';
import IvBagPanel from './IvBagPanel';
import IvControlPanel from './IvControlPanel';
import IvTypesPanel from './IvTypesPanel';

const ALL_PANELS = ['IV_BAG', 'IV_CONTROL', 'IV_TYPES' ];

export default {
  name: 'case-ivs',
  components: {
    HelpTooltip,
    Panel,
    IvDiagram,
    ToggleSwitch,
    IvBagPanel,
    IvControlPanel,
    IvTypesPanel,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      state: {
        openPanels: this.caseConf.iv.enabled ? ALL_PANELS : [],
      },
      defaultIv: {
        enabled: false,
        bagCount: this.$featureAvailabilityService.getLimit('MAX_BAGS_AVAILABLE'),
        allowPorts: false,
        allowMultiLumen: false,
        dropSet: {
          enabled: false,
          minGTTM: 0,
          maxGTTM: 1000,
          dripSet: [],
        },
        pump: {
          enabled: false,
          allowTitrate: false,
          allowOptions: false,
        },
        types: [],
      },
    };
  },
  computed: {
    ivEnabledError() {
      const errors = this.$store
        ? get(
            this.$store.getters['validation/errorsByFieldAndId'],
            'iv.enabled',
            [],
          )
        : [];
      return errors.length > 0 ? errors[0] : null;
    },
  },
  watch: {
    navigation: {
      handler() {
        this.navigationHandler();
      },
      deep: true
    },
  },
  created() {
    this.emitUpdate = debounce(this.emitUpdate);
  },
  mounted() {
    this.navigationHandler();
  },
  methods: {
    navigationHandler() {
      if (this.navigation && this.navigation.params) {
        if (this.isIvOnAtStartNavigation(this.navigation)) {
          this.$eventHub.$emit('UI::OPEN_ON_AT_START_PANEL', this.navigation);
        }

        if (this.isTypesNavigation(this.navigation)) {
          if (!this.isActivePanel('IV_TYPES')) this.togglePanel('IV_TYPES');
        }
      }
    },
    isActivePanel(panel) {
      return this.state.openPanels.includes(panel);
    },
    togglePanel(panel) {
      if (this.isActivePanel(panel)) {
        this.state.openPanels = this.state.openPanels.filter(p => p !== panel);
      } else {
        this.state.openPanels.push(panel);
      }
    },
    isIvOnAtStartNavigation(navObject) {
      return (
        navObject &&
        navObject.category === 'ivs' &&
        navObject.params.field === 'initialIvs'
      );
    },
    isTypesNavigation(navObject) {
      return (
        navObject &&
        navObject.category === 'ivs' &&
        navObject.params &&
        navObject.params.type
      );
    },
    updateEnabled(enabled) {
      let newIv;
      if (enabled && !this.caseConf.iv.types) {
        // If iv subfields don't exist, set them to default values
        newIv = merge({}, this.caseConf.iv, this.defaultIv);
      } else {
        newIv = this.caseConf.iv;
      }
      if (enabled && !this.state.openPanels.length) {
        this.state.openPanels = ALL_PANELS;
      }
      if (!enabled) {
        this.state.openPanels = [];
      }
      newIv = merge({}, newIv, { enabled });
      this.emitUpdate(newIv);
    },
    emitUpdate(iv) {
      this.$emit('update', { iv });
    },
  },
};
</script>

<style scoped>
.case-ivs {
  height: 100%;
  overflow: auto;
}

.case-ivs >>> .panel-content-container {
  padding-bottom: 0;
}

.case-ivs >>> .panel-content {
  display: flex;
  flex-direction: row;
}

.left-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.accordion {
  width: 100%;
  padding-left: 1rem;
}

.accordion-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}

.accordion-trigger {
  background-color: #fcfcfd !important;
  margin: 0;
  text-align: left;
  height: 40px;
  z-index: 3;
}
.accordion-trigger.open .trigger-icon {
  transform: rotateZ(90deg);
}
.accordion-trigger label {
  vertical-align: middle;
}

.accordion-content {
  background-color: #fcfcfd;
  margin-left: 20px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.25);
  border-radius: 0px 0px 4px 4px;
  margin-top: -10px;
  margin-bottom: 15px;
  padding-top: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: row;
}

.fold-enter-active,
.fold-leave-active {
  transition: max-height 0.5s;
}
.fold-enter, .fold-leave-to /* .fade-leave-active below version 2.1.8 */ {
  max-height: 0;
}

.fold-enter-to,
.fold-leave {
  max-height: 300px;
}

.on-at-start-panel-container {
  padding-top: 12px;
}
</style>

<style>
.case-ivs .accordion-trigger .md-ripple {
  justify-content: flex-start;
}
</style>
